import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { networkManager, getErrorMessage } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Loader2, Trophy, Eye, EyeOff, Users, Award, Download, X } from 'lucide-react';

/** Matches backend LeaderboardEntry from score.service.ts */
interface LeaderboardEntry {
  rank: number;
  teamId: string;
  teamName: string;
  theme: string;
  averageScore: number;
  judgeCount: number;
  badges: string[];
  productName?: string;
  productSummary?: string;
  feedbackSummary?: { text: string; generatedAt: string; basedOnScoreCount: number };
  teamPhotoUrl?: string;
}

type Visibility = 'hidden' | 'judges_only' | 'public';

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100 text-lg" title="1st Place">
        🥇
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-lg" title="2nd Place">
        🥈
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-lg" title="3rd Place">
        🥉
      </span>
    );
  }
  return (
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-sunken text-sm font-semibold text-ink-secondary">
      {rank}
    </span>
  );
}

export function LeaderboardPage() {
  const { eventId } = useParams();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const isCeremony = searchParams.get('ceremony') === 'true';
  const { can } = useAuth();
  const canManage = can('leaderboard:manage');
  const canExport = can('bulk:export');

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<Visibility>('hidden');
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  // Badge awarding state
  const [badgeTeamId, setBadgeTeamId] = useState<string | null>(null);
  const [badgeName, setBadgeName] = useState('');
  const [awardingBadge, setAwardingBadge] = useState(false);
  const [exportingLeaderboard, setExportingLeaderboard] = useState(false);

  useEffect(() => {
    Promise.all([
      networkManager.get<{ status: boolean; data: LeaderboardEntry[] }>(
        `/api/events/${eventId}/scores/leaderboard`,
      ),
      networkManager.get<{ status: boolean; data: { leaderboardVisibility?: string } }>(
        `/api/events/${eventId}`,
      ).catch(() => ({ data: { leaderboardVisibility: 'hidden' } })),
    ])
      .then(([lbRes, eventRes]) => {
        setEntries(lbRes.data);
        setVisibility((eventRes.data.leaderboardVisibility as Visibility) ?? 'hidden');
      })
      .catch((err) => {
        setError(getErrorMessage(err, 'Failed to load leaderboard'));
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  async function handleVisibilityChange(newVisibility: Visibility) {
    setUpdatingVisibility(true);
    try {
      await networkManager.put<{ status: boolean; data: { leaderboardVisibility: string } }>(
        `/api/events/${eventId}/scores/leaderboard`,
        { visibility: newVisibility },
      );
      setVisibility(newVisibility);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update visibility'));
    } finally {
      setUpdatingVisibility(false);
    }
  }

  async function handleAwardBadge() {
    if (!badgeTeamId || !badgeName.trim()) return;
    setAwardingBadge(true);
    try {
      await networkManager.post<{ status: boolean; data: { teamId: string; badge: string; awardedAt: string } }>(
        `/api/events/${eventId}/scores/badges`,
        { teamId: badgeTeamId, badge: badgeName.trim() },
      );
      // Update local state to show the new badge
      setEntries((prev) =>
        prev.map((e) =>
          e.teamId === badgeTeamId
            ? { ...e, badges: [...e.badges, badgeName.trim()] }
            : e,
        ),
      );
      setBadgeTeamId(null);
      setBadgeName('');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to award badge'));
    } finally {
      setAwardingBadge(false);
    }
  }

  async function handleExportLeaderboard() {
    setExportingLeaderboard(true);
    try {
      const res = await networkManager.post<{
        status: boolean;
        data: { leaderboard: LeaderboardEntry[]; exportedAt: string };
      }>('/api/bulk/export/leaderboard', { eventId });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leaderboard-${eventId}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to export leaderboard'));
    } finally {
      setExportingLeaderboard(false);
    }
  }

  // Backend already returns ranked entries sorted
  const ranked = entries;

  if (loading) {
    return (
      <div className={`flex justify-center py-12 ${isCeremony ? 'min-h-screen bg-gray-950' : ''}`}>
        <Loader2 className={`h-8 w-8 animate-spin ${isCeremony ? 'text-gray-500' : 'text-ink-tertiary'}`} />
      </div>
    );
  }

  // Ceremony mode — full-screen dark reveal, top 10 only.
  // Reveal order is 10th → 1st so the 1st-place team lands last for maximum
  // drama. Each team gets its photo (if uploaded) as a hero element.
  if (isCeremony) {
    const MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };
    const CEREMONY_CAP = 10;
    const ceremonyEntries = ranked.slice(0, CEREMONY_CAP);

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 px-4 py-10 sm:px-8 sm:py-14">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-yellow-400/80">
              The results are in
            </p>
            <h1 className="mt-3 font-display text-5xl font-extrabold text-white sm:text-7xl">
              Leaderboard
            </h1>
            <p className="mt-3 text-base text-gray-400 sm:text-lg">
              Top {ceremonyEntries.length} team{ceremonyEntries.length === 1 ? '' : 's'} · revealing in reverse order
            </p>
          </div>

          {ranked.length === 0 ? (
            <p className="mt-16 text-center text-2xl text-gray-500">No scores submitted yet</p>
          ) : (
            <div className="mt-12 space-y-4">
              {ceremonyEntries.map((entry, i) => {
                // 1st place lives at the top of the list, but reveal goes
                // bottom-up for drama: 10th animates in first, 1st last.
                const reverseIndex = ceremonyEntries.length - 1 - i;
                const medal = MEDALS[entry.rank];
                const isFirst = entry.rank === 1;
                const isTop3 = entry.rank <= 3;
                const photo = entry.teamPhotoUrl;
                // Size treatment: 1st is hero-sized, 2-3 slightly larger, rest compact.
                const tileClasses = isFirst
                  ? 'gap-6 rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-yellow-500/20 via-amber-500/10 to-yellow-900/30 border-2 border-yellow-400 shadow-[0_0_60px_-10px_rgba(234,179,8,0.45)]'
                  : entry.rank === 2
                  ? 'gap-5 rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-gray-500/20 to-gray-800/40 border-2 border-gray-300/70'
                  : entry.rank === 3
                  ? 'gap-5 rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-orange-600/25 to-orange-900/40 border-2 border-orange-500/80'
                  : 'gap-4 rounded-2xl p-4 sm:p-5 bg-gray-800/60 border border-gray-700/70';
                const avatarSize = isFirst
                  ? 'h-24 w-24 sm:h-32 sm:w-32'
                  : isTop3
                  ? 'h-20 w-20 sm:h-24 sm:w-24'
                  : 'h-14 w-14 sm:h-16 sm:w-16';
                const avatarRing = isFirst
                  ? 'ring-4 ring-yellow-400/70'
                  : entry.rank === 2
                  ? 'ring-4 ring-gray-300/50'
                  : entry.rank === 3
                  ? 'ring-4 ring-orange-500/60'
                  : 'ring-2 ring-gray-700';
                const nameClasses = isFirst
                  ? 'text-3xl sm:text-5xl'
                  : isTop3
                  ? 'text-2xl sm:text-3xl'
                  : 'text-lg sm:text-xl';
                const scoreClasses = isFirst
                  ? 'text-4xl sm:text-6xl text-yellow-300'
                  : isTop3
                  ? 'text-3xl sm:text-4xl text-white'
                  : 'text-xl sm:text-2xl text-gray-300';
                // Placeholder if no photo: team initials on a branded gradient so
                // every row still has a visual anchor.
                const initials = entry.teamName
                  .split(/\s+/)
                  .map((w) => w[0])
                  .filter(Boolean)
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();
                return (
                  <div
                    key={entry.teamId}
                    className="opacity-0 animate-[fadeInUp_0.6s_ease-out_forwards]"
                    style={{ animationDelay: `${reverseIndex * 500}ms` }}
                  >
                    <div className={`flex items-center ${tileClasses}`}>
                      {/* Avatar / photo / placeholder */}
                      <div className={`relative shrink-0 overflow-hidden rounded-full ${avatarSize} ${avatarRing}`}>
                        {photo ? (
                          <img
                            src={photo}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="eager"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-500/40 to-purple-700/60 font-display text-xl font-bold text-white sm:text-2xl">
                            {initials || '?'}
                          </div>
                        )}
                        {/* Rank chip overlaid on the avatar */}
                        <div className={`absolute -bottom-1 -right-1 flex items-center justify-center rounded-full border-2 border-gray-950 bg-gray-900 ${isTop3 ? 'h-10 w-10 text-xl' : 'h-8 w-8 text-sm'} font-bold text-white`}>
                          {medal || `${entry.rank}`}
                        </div>
                      </div>

                      {/* Name + theme + badges */}
                      <div className="min-w-0 flex-1">
                        <h2 className={`font-display font-extrabold text-white truncate ${nameClasses}`}>
                          {entry.teamName}
                        </h2>
                        {entry.productName && (
                          <p className={`${isTop3 ? 'text-base sm:text-lg' : 'text-sm'} truncate text-gray-300`}>
                            {entry.productName}
                          </p>
                        )}
                        <p className={`${isTop3 ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} mt-0.5 text-gray-400 truncate`}>
                          {entry.theme}
                        </p>
                        {entry.badges.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {entry.badges.map((b, j) => (
                              <span key={j} className="inline-flex items-center gap-1 rounded-full bg-yellow-900/60 px-2.5 py-0.5 text-xs font-medium text-yellow-300">
                                <Award className="h-3 w-3" /> {b}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Score */}
                      <div className="shrink-0 text-right">
                        <p className={`font-mono font-bold ${scoreClasses}`}>
                          {entry.averageScore.toFixed(2)}
                        </p>
                        <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                          {entry.judgeCount} judge{entry.judgeCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <Link to={`/events/${eventId}`} className="btn-ghost mb-4 inline-flex">
        <ArrowLeft className="h-4 w-4" /> Back to Event
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Trophy className="h-7 w-7 text-yellow-500" />
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Leaderboard</h1>
        </div>
        {canManage && ranked.length > 0 && (
          <a
            href={`/events/${eventId}/leaderboard?ceremony=true`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
          >
            <Trophy className="h-4 w-4" /> Ceremony Mode
          </a>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Organizer visibility toggle */}
      {canManage && (
        <div className="mt-4 card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-ink">Visibility:</span>
            {(
              [
                { value: 'hidden', label: 'Hidden', icon: EyeOff },
                { value: 'judges_only', label: 'Judges Only', icon: Users },
                { value: 'public', label: 'Public', icon: Eye },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleVisibilityChange(opt.value)}
                disabled={updatingVisibility}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  visibility === opt.value
                    ? 'bg-accent text-white'
                    : 'bg-surface-sunken text-ink-secondary hover:text-ink'
                }`}
              >
                {updatingVisibility ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <opt.icon className="h-3.5 w-3.5" />
                )}
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Export button */}
      {canExport && ranked.length > 0 && (
        <div className="mt-4 flex gap-2">
          <button
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            onClick={handleExportLeaderboard}
            disabled={exportingLeaderboard}
          >
            {exportingLeaderboard ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export JSON
          </button>
        </div>
      )}

      {/* Empty states */}
      {!error && entries.length === 0 && (
        <div className="mt-8 py-12 text-center">
          <Trophy className="mx-auto h-12 w-12 text-ink-tertiary" />
          {canManage ? (
            <p className="mt-4 text-ink-secondary">
              No scores yet — judging hasn't started
            </p>
          ) : (
            <p className="mt-4 text-ink-secondary">
              Leaderboard will be revealed after judging
            </p>
          )}
        </div>
      )}

      {/* Leaderboard table - desktop */}
      {ranked.length > 0 && (
        <>
          <div className="mt-6 hidden sm:block">
            <div className="card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ring bg-surface-sunken">
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary w-16">Rank</th>
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Team</th>
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Theme</th>
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Badges</th>
                    <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Avg Score</th>
                    <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Judges</th>
                    {canManage && (
                      <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((entry) => (
                    <tr key={entry.teamId} className="border-b border-ring last:border-0">
                      <td className="px-4 py-3">
                        <RankBadge rank={entry.rank} />
                      </td>
                      <td className="px-4 py-3 font-medium text-ink">
                        <Link
                          to={`/events/${eventId}/teams/${entry.teamId}`}
                          className="hover:text-accent transition-colors"
                        >
                          {entry.teamName}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="badge bg-accent-subtle text-ink">{entry.theme}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {entry.badges.map((badge, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-2xs font-medium text-yellow-800">
                              <Award className="h-3 w-3" /> {badge}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-ink">
                        {entry.averageScore.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-ink-secondary">
                        {entry.judgeCount}
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <button
                            className="btn-ghost text-xs inline-flex items-center gap-1"
                            onClick={() => { setBadgeTeamId(entry.teamId); setBadgeName(''); }}
                          >
                            <Award className="h-3.5 w-3.5" /> Award Badge
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Leaderboard cards - mobile */}
          <div className="mt-6 space-y-3 sm:hidden">
            {ranked.map((entry) => (
              <div key={entry.teamId} className="card p-4">
                <Link
                  to={`/events/${eventId}/teams/${entry.teamId}`}
                  className="flex items-center gap-4 transition-shadow hover:shadow-card-hover"
                >
                  <RankBadge rank={entry.rank} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{entry.teamName}</p>
                    <span className="badge mt-1 bg-accent-subtle text-ink text-xs">
                      {entry.theme}
                    </span>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-semibold text-ink">{entry.averageScore.toFixed(2)}</p>
                    <p className="text-xs text-ink-tertiary">{entry.judgeCount} judge{entry.judgeCount !== 1 ? 's' : ''}</p>
                  </div>
                </Link>
                {entry.badges.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.badges.map((badge, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-2xs font-medium text-yellow-800">
                        <Award className="h-3 w-3" /> {badge}
                      </span>
                    ))}
                  </div>
                )}
                {canManage && (
                  <button
                    className="btn-ghost mt-2 text-xs inline-flex items-center gap-1"
                    onClick={() => { setBadgeTeamId(entry.teamId); setBadgeName(''); }}
                  >
                    <Award className="h-3.5 w-3.5" /> Award Badge
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Award Badge Modal */}
      {badgeTeamId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card mx-4 w-full max-w-sm p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">Award Badge</h2>
              <button className="btn-ghost" onClick={() => setBadgeTeamId(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-sm text-ink-secondary">
              Team: {entries.find((e) => e.teamId === badgeTeamId)?.teamName ?? badgeTeamId}
            </p>
            <input
              type="text"
              className="input-base mt-3"
              placeholder="Badge name (e.g. Best Design, Most Innovative)"
              value={badgeName}
              onChange={(e) => setBadgeName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAwardBadge()}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setBadgeTeamId(null)}>
                Cancel
              </button>
              <button
                className="btn-primary inline-flex items-center gap-1.5"
                onClick={handleAwardBadge}
                disabled={awardingBadge || !badgeName.trim()}
              >
                {awardingBadge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
                Award
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
